/**
 * ProvenanceGraph — deterministic SVG visualization of a small provenance DAG.
 *
 * Single responsibility: render nodes and edges with a stable layered layout
 * derived from the input order. Intentionally minimal — no virtualization,
 * no panning — matching the small DAGs PDBG demos exhibit. Larger graphs
 * should use ELK or a dedicated library, not this component.
 *
 * Migration: new call-sites should prefer `ProvenanceDiagram`
 * (`./typed-diagrams/ProvenanceDiagram.tsx`), the React Flow rewrite
 * with feature parity, zoom, pan, and fit-to-view. This SVG
 * implementation remains for screens that snapshot the rendered SVG
 * geometry. Tracked by issue #1580.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY, PRIMARY, SAFE, WARN } from "../design-system/tokens.js";

export interface ProvenanceGraphNode {
  readonly id: string;
  readonly label: string;
  readonly kind: "input" | "policy_eval" | "witness" | "effect";
  readonly tone?: "neutral" | "highlight" | "alert";
}

export interface ProvenanceGraphEdge {
  readonly from: string;
  readonly to: string;
  readonly label?: string;
}

export interface ProvenanceGraphProps {
  readonly nodes: readonly ProvenanceGraphNode[];
  readonly edges: readonly ProvenanceGraphEdge[];
  /** Optional caption rendered below the graph. */
  readonly caption?: string;
}

const NODE_WIDTH = 140;
const NODE_HEIGHT = 48;
const COLUMN_GAP = 60;
const ROW_GAP = 24;

const KIND_FILL: Readonly<Record<ProvenanceGraphNode["kind"], string>> = {
  input: PRIMARY[100],
  policy_eval: PRIMARY[300],
  witness: SAFE[300],
  effect: GRAY[300],
} as const;

const TONE_BORDER: Readonly<Record<NonNullable<ProvenanceGraphNode["tone"]>, string>> = {
  neutral: GRAY[400],
  highlight: PRIMARY[700],
  alert: WARN[700],
} as const;

interface PositionedNode extends ProvenanceGraphNode {
  readonly x: number;
  readonly y: number;
}

/**
 * Compute a deterministic layered layout: each node is placed in the column
 * matching its longest predecessor chain length, ordered by appearance.
 */
function layoutNodes(
  nodes: readonly ProvenanceGraphNode[],
  edges: readonly ProvenanceGraphEdge[],
): readonly PositionedNode[] {
  const knownIds = new Set<string>();
  const incoming = new Map<string, string[]>();
  for (const node of nodes) {
    knownIds.add(node.id);
    incoming.set(node.id, []);
  }
  // Only edges whose endpoints are both present in the node set contribute to
  // the layered layout; orphaned edges are also skipped at render time, so
  // ignoring them here keeps layout and rendering aligned.
  for (const edge of edges) {
    if (!knownIds.has(edge.from) || !knownIds.has(edge.to)) continue;
    incoming.get(edge.to)?.push(edge.from);
  }

  const layer = new Map<string, number>();
  function depth(id: string, seen: Set<string>): number {
    const cached = layer.get(id);
    if (cached !== undefined) return cached;
    if (seen.has(id)) return 0;
    const preds = incoming.get(id) ?? [];
    if (preds.length === 0) {
      layer.set(id, 0);
      return 0;
    }
    seen.add(id);
    let maxDepth = 0;
    for (const pred of preds) {
      maxDepth = Math.max(maxDepth, depth(pred, seen));
    }
    seen.delete(id);
    const d = 1 + maxDepth;
    layer.set(id, d);
    return d;
  }
  for (const node of nodes) depth(node.id, new Set());

  const byLayer = new Map<number, ProvenanceGraphNode[]>();
  for (const node of nodes) {
    const d = layer.get(node.id) ?? 0;
    const list = byLayer.get(d);
    if (list === undefined) byLayer.set(d, [node]);
    else list.push(node);
  }

  const positioned: PositionedNode[] = [];
  for (const [d, list] of byLayer.entries()) {
    list.forEach((node, i) => {
      positioned.push({
        ...node,
        x: d * (NODE_WIDTH + COLUMN_GAP),
        y: i * (NODE_HEIGHT + ROW_GAP),
      });
    });
  }
  return positioned;
}

/**
 * Renders a small provenance DAG. Layout is deterministic, so the same input
 * always produces the same SVG — important for snapshot testing.
 */
export const ProvenanceGraph = React.memo(function ProvenanceGraph({
  nodes,
  edges,
  caption,
}: ProvenanceGraphProps): React.JSX.Element {
  // Per-instance id keeps the SVG <marker> reference stable when multiple
  // graphs render on the same page (raw "provenance-arrow" would collide).
  const reactId = React.useId();
  const arrowMarkerId = `provenance-arrow-${reactId}`;
  // `layoutNodes` is a recursive DAG traversal; memoize the whole geometry
  // pass so an unrelated parent re-render does not re-run it. It recomputes
  // only when the `nodes` / `edges` references change.
  const { positioned, positionsById, width, height } = React.useMemo(() => {
    const positioned = layoutNodes(nodes, edges);
    const positionsById = new Map<string, PositionedNode>();
    let maxX = 0;
    let maxY = 0;
    for (const node of positioned) {
      positionsById.set(node.id, node);
      if (node.x > maxX) maxX = node.x;
      if (node.y > maxY) maxY = node.y;
    }
    maxX += NODE_WIDTH;
    maxY += NODE_HEIGHT;
    return {
      positioned,
      positionsById,
      width: Math.max(maxX, NODE_WIDTH) + 8,
      height: Math.max(maxY, NODE_HEIGHT) + 8,
    };
  }, [nodes, edges]);

  return (
    <figure style={{ margin: 0 }}>
      <svg
        role="img"
        aria-label="Provenance graph"
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", maxWidth: `${width}px`, height: "auto" }}
      >
        <defs>
          <marker
            id={arrowMarkerId}
            viewBox="0 0 10 10"
            refX="10"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={GRAY[500]} />
          </marker>
        </defs>

        {edges.map((edge) => {
          const from = positionsById.get(edge.from);
          const to = positionsById.get(edge.to);
          if (from === undefined || to === undefined) return null;
          const x1 = from.x + NODE_WIDTH;
          const y1 = from.y + NODE_HEIGHT / 2;
          const x2 = to.x;
          const y2 = to.y + NODE_HEIGHT / 2;
          return (
            <g key={`${edge.from}->${edge.to}`}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={GRAY[500]}
                strokeWidth={1.5}
                markerEnd={`url(#${arrowMarkerId})`}
              />
              {edge.label !== undefined && (
                <text
                  x={(x1 + x2) / 2}
                  y={(y1 + y2) / 2 - 4}
                  fontSize="10"
                  fill={GRAY[500]}
                  textAnchor="middle"
                  fontFamily={FONT.mono}
                >
                  {edge.label}
                </text>
              )}
            </g>
          );
        })}

        {positioned.map((node) => (
          <g key={node.id} transform={`translate(${node.x}, ${node.y})`}>
            <rect
              width={NODE_WIDTH}
              height={NODE_HEIGHT}
              rx={6}
              ry={6}
              fill={KIND_FILL[node.kind]}
              stroke={TONE_BORDER[node.tone ?? "neutral"]}
              strokeWidth={node.tone === "neutral" || node.tone === undefined ? 1 : 2}
            />
            <text
              x={NODE_WIDTH / 2}
              y={NODE_HEIGHT / 2 - 4}
              fontSize="11"
              fontWeight="600"
              fill={GRAY[900]}
              textAnchor="middle"
              fontFamily={FONT.body}
            >
              {node.label}
            </text>
            <text
              x={NODE_WIDTH / 2}
              y={NODE_HEIGHT / 2 + 12}
              fontSize="9"
              fill={GRAY[600]}
              textAnchor="middle"
              fontFamily={FONT.mono}
            >
              {node.kind}
            </text>
          </g>
        ))}
      </svg>
      {caption !== undefined && (
        <figcaption
          style={{
            color: GRAY[500],
            fontSize: "0.75rem",
            marginTop: "0.25rem",
            textAlign: "center",
          }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
});
